import { isDefined } from "@clipboard-health/util-ts";
import {
  DeploymentEnvironmentName,
  environmentConfig,
  isDevelopmentNodeEnvironment,
} from "@src/appV2/environment";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { UserLocationState } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQuery } from "@tanstack/react-query";
import constate from "constate";
import { HyperTrack, type Subscription } from "hypertrack-sdk-ionic-capacitor";
import { useEffect, useMemo, useState } from "react";

import { useLazyIsInsideGeofence } from "../hooks/useIsInsideGeofence";
import { orderHandleForShift } from "../providers/hypertrackLocationProvider";
import { type GeoLocation } from "../types";

const GEOFENCE_POLLING_INTERVAL_MS = 10_000;

function isDevelopmentEnvironment(): boolean {
  return (
    isDevelopmentNodeEnvironment() ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.DEVELOPMENT ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.LOCAL ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.STAGING ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PROD_SHADOW
  );
}

// Development helper for web testing
const WEB_GEOFENCE_DEBUG_KEY = "cbh-web-geofence-debug";
function getWebGeofenceOverride(): boolean | undefined {
  if (typeof window === "undefined" || isCapacitorPlatform() || !isDevelopmentEnvironment()) {
    return undefined;
  }

  const stored = localStorage.getItem(WEB_GEOFENCE_DEBUG_KEY);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return stored ? JSON.parse(stored) : undefined;
}

function setWebGeofenceOverride(inside: boolean | undefined): void {
  if (typeof window === "undefined" || isCapacitorPlatform() || !isDevelopmentEnvironment()) {
    return;
  }

  if (inside === undefined) {
    localStorage.removeItem(WEB_GEOFENCE_DEBUG_KEY);
  } else {
    localStorage.setItem(WEB_GEOFENCE_DEBUG_KEY, JSON.stringify(inside));
  }
}

/**
 * Expose debug functions globally for web testing (development mode only)
 *
 * These controls only affect the `useGeofence` hook behavior and allow overriding
 * geofence detection for testing clock-in/out workflows without physical location changes.
 *
 * Usage in browser console during development:
 * - `window.cbhGeofenceDebug.setInside(true)` - Force geofence status to "inside"
 * - `window.cbhGeofenceDebug.setOutside()` - Force geofence status to "outside"
 * - `window.cbhGeofenceDebug.clearOverride()` - Clear override and resume normal detection
 * - `window.cbhGeofenceDebug.getOverride()` - Check current override status
 *
 * Requirements:
 * - Only available in development mode (NODE_ENV=development or API environment=development/local)
 * - Only available on web platforms (not native apps)
 * - Only affects components using the `useGeofence` hook
 * - Overrides persist across page refreshes until cleared
 */
if (typeof window !== "undefined" && !isCapacitorPlatform() && isDevelopmentEnvironment()) {
  (window as any).cbhGeofenceDebug = {
    setInside: (inside: boolean) => {
      setWebGeofenceOverride(inside);
    },
    setOutside: () => {
      setWebGeofenceOverride(false);
    },
    clearOverride: () => {
      setWebGeofenceOverride(undefined);
    },
    getOverride: getWebGeofenceOverride,
  };
}

export interface UseGeofenceProps {
  shiftId: string;
  workerId: string;
  initialLocation: UserLocationState | undefined;
  fallbackGeofenceLocation: GeoLocation | undefined;
  enableGeofenceTracking?: boolean;
}

function useGeofence(props: UseGeofenceProps) {
  const {
    shiftId,
    workerId,
    initialLocation = UserLocationState.OUTSIDE_GEOFENCE,
    fallbackGeofenceLocation,
    enableGeofenceTracking = false,
  } = props;

  const worker = useDefinedWorker();
  const isSupportPairingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_SUPPORT_PAIRING, {
    defaultValue: false,
  });

  const isHyperTrackEnabled = useCbhFlag(CbhFeatureFlag.HYPERTRACK_CLOCK_IN_OUT, {
    defaultValue: false,
  });

  const isNative = isCapacitorPlatform();

  // Test account override: always inside when test account + support pairing enabled
  const isTestAccountOverride = worker.isTestAccount === true && isSupportPairingEnabled;

  // Determine if we can subscribe to HyperTrack based on current conditions
  const canSubscribeToHyperTrack = useMemo(() => {
    return isNative && isHyperTrackEnabled && enableGeofenceTracking;
  }, [isNative, isHyperTrackEnabled, enableGeofenceTracking]);

  const [hyperTrackFailed, setHyperTrackFailed] = useState(false);
  const [inside, setInside] = useState(
    isTestAccountOverride || initialLocation === UserLocationState.INSIDE_GEOFENCE
  );

  const getIsInsideGeofence = useLazyIsInsideGeofence({
    requestUniqueId: {
      workerId,
      shiftId,
    },
    action: "calculate-driving-distance",
    fallbackGeofenceLocation,
  });

  const query = useQuery({
    queryKey: ["location/geofence", { shiftId, workerId }],
    queryFn: async () => {
      const startEvent = {
        shiftId,
        workerId,
        method: "fallback_polling",
      };
      logEvent(APP_V2_USER_EVENTS.GEOFENCE_POLLING_STARTED, startEvent);

      try {
        const result = await getIsInsideGeofence();

        const completedEvent = {
          shiftId,
          workerId,
          method: "fallback_polling",
          isInsideGeofence: result?.data?.isInsideGeofence,
          success: true,
        };
        logEvent(APP_V2_USER_EVENTS.GEOFENCE_POLLING_COMPLETED, completedEvent);

        return result;
      } catch (error) {
        const errorEvent = {
          error,
          metadata: {
            shiftId,
            workerId,
            method: "fallback_polling",
          },
        };
        logError(APP_V2_APP_EVENTS.GEOFENCE_POLLING_FAILED, errorEvent);
        throw error;
      }
    },
    enabled: !isTestAccountOverride && (!canSubscribeToHyperTrack || hyperTrackFailed), // Skip polling when test account override is active
    refetchInterval: GEOFENCE_POLLING_INTERVAL_MS,
    staleTime: GEOFENCE_POLLING_INTERVAL_MS,
    cacheTime: GEOFENCE_POLLING_INTERVAL_MS,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (isTestAccountOverride || !canSubscribeToHyperTrack || hyperTrackFailed) {
      return;
    }

    let ordersUnsubscribe: Subscription | undefined;

    async function setupSubscriptions() {
      try {
        const setupStartEvent = {
          shiftId,
          workerId,
        };
        logEvent(APP_V2_USER_EVENTS.HYPERTRACK_SUBSCRIPTION_SETUP_STARTED, setupStartEvent);

        HyperTrack.setWorkerHandle(workerId);

        ordersUnsubscribe = HyperTrack.subscribeToOrders(async (orders) => {
          const orderHandle = orderHandleForShift({ shiftId, workerId });
          const order = orders.get(orderHandle);

          const orderReceivedEvent = {
            shiftId,
            workerId,
            orderHandle,
            hasOrder: !!order,
          };
          logEvent(APP_V2_USER_EVENTS.HYPERTRACK_ORDER_RECEIVED, orderReceivedEvent);

          const isInsideGeofence = await order?.isInsideGeofence();

          if (isInsideGeofence?.type !== "success") {
            const checkFailedEvent = {
              shiftId,
              workerId,
              resultType: isInsideGeofence?.type,
            };
            logEvent(APP_V2_USER_EVENTS.HYPERTRACK_GEOFENCE_CHECK_FAILED, checkFailedEvent);
            // trigger GPS fallback
            setHyperTrackFailed(true);
            return;
          }

          logEvent(APP_V2_USER_EVENTS.GEOFENCE_STATUS_CHANGED, {
            shiftId,
            workerId,
            method: "hypertrack_subscription",
            previousInside: inside,
            newInside: isInsideGeofence.value,
          });

          setInside(isInsideGeofence.value);
        });

        const setupCompletedEvent = {
          shiftId,
          workerId,
        };
        logEvent(APP_V2_USER_EVENTS.HYPERTRACK_SUBSCRIPTION_SETUP_COMPLETED, setupCompletedEvent);
      } catch (error) {
        // trigger GPS fallback
        setHyperTrackFailed(true);
        const setupFailedEvent = {
          error,
          metadata: {
            shiftId,
            workerId,
          },
        };
        logError(APP_V2_APP_EVENTS.HYPERTRACK_SUBSCRIPTION_SETUP_FAILED, setupFailedEvent);
      }
    }

    void setupSubscriptions();

    // eslint-disable-next-line consistent-return
    return function () {
      void ordersUnsubscribe?.remove();
    };
  }, [
    canSubscribeToHyperTrack,
    hyperTrackFailed,
    workerId,
    shiftId,
    inside,
    isTestAccountOverride,
  ]);

  // Use fallback data when not using subscriptions
  useEffect(() => {
    if (isTestAccountOverride || (canSubscribeToHyperTrack && !hyperTrackFailed)) {
      return;
    }

    const webOverride = getWebGeofenceOverride();

    const fallbackInside =
      webOverride ??
      ((query.data?.data?.isInsideGeofence ?? false) ||
        initialLocation === UserLocationState.INSIDE_GEOFENCE);

    // Log when fallback geofence status changes
    if (fallbackInside !== inside) {
      logEvent(APP_V2_USER_EVENTS.GEOFENCE_STATUS_CHANGED, {
        shiftId,
        workerId,
        method: isDefined(webOverride) ? "web_debug_override" : "fallback_polling",
        previousInside: inside,
        newInside: fallbackInside,
        queryDataExists: !!query.data,
        initialLocationFallback: initialLocation === UserLocationState.INSIDE_GEOFENCE,
        webDebugOverride: webOverride,
      });
    }

    setInside(fallbackInside);
  }, [
    canSubscribeToHyperTrack,
    hyperTrackFailed,
    query.data,
    query.dataUpdatedAt,
    initialLocation,
    inside,
    shiftId,
    workerId,
    isTestAccountOverride,
  ]);

  return { inside };
}

export const [GeofenceProvider, useGeofenceContext] = constate(useGeofence);
